import { LuminateError } from '@frx/frx-services';

import currency from 'currency.js';
import camelCase from 'lodash/fp/camelCase';
import upperFirst from 'lodash/fp/upperFirst';
import { EventTypes } from './constants/constants';

export const trimZeroDecimals = (value: string): string =>
  value.replace('.00', '');

export const convertCentsToDollars = (cents: number): number => cents / 100;

export const isDev = window.location.hostname === 'localhost';

export const isQA = window.location.hostname === 'fundraising.qa.stjude.org';

export const isProd = window.location.hostname === 'fundraising.stjude.org';

/**
 *
 * @param array the array to search for unique properties
 * @param keys array of keys within the object you want to filter by
 * @returns array of unique objects by passed keys
 */
export const getUniqueObjectsInArrayByKey = (
  array: [],
  keys: any[] = []
): any[] =>
  Array.from(
    new Map(
      array.map((item) => [keys.map((key) => item[key]).join('|'), item])
    ).values()
  );

/**
 * @description Formats a cent amount with a $ prefix
 * If no cents exists, the cents amount is not displayed
 * @param centsAmount
 * Examples -
 * 1 => $0.01
 * 9.99 => $9.99
 * 20 => $20
 * */
export const formatDollarAmount = (centsAmount: number): string =>
  trimZeroDecimals(
    currency(Math.round(centsAmount), {
      fromCents: true,
    }).format()
  );

/**
 * @description Formats a dollar amount, but WITHOUT a $ prefix
 * If no cents exists, the cents amount is not displayed
 *  Examples -
 * 1 => 0.01
 * 9.99 => 9.99
 * 20 => 20
 * @param amount
 * */
export const formatAmount = (centsAmount: number): string =>
  trimZeroDecimals(
    currency(Math.round(centsAmount), {
      fromCents: true,
      symbol: '',
    }).format()
  );

export const shuffleArray = (array: any[]): any[] =>
  array.sort(() => Math.random() - 0.5);

/**
 * @description
 * Identifies if the error is provided by TeamRaiser
 * If so, there wil be a somewhat user facing message that can be presented to the user
 * http://open.convio.com/api/apidoc/general/error_codes.html
 * @param code error code
 * @returns boolean
 */
export const getTeamRaiserErrorMessage = (params: {
  error: LuminateError | null;
  defaultErrorMessage: string;
}): string => {
  const { error, defaultErrorMessage } = params;

  if (!error) return '';

  const { code, message } = error;
  if (code >= 2601 && code <= 2730) {
    return message;
  }

  return defaultErrorMessage;
};

/**
 * @description
 * sanitize birth date for user birth date format
 * @param date
 * @return string
 */
export const sanitizeBirthDate = (date: string): string => {
  const split = date.split('-');
  const slice = split.slice(0, split.length - 1);
  return slice.join('-');
};

export const getDataTracking = (value = '') => ({
  [`data-tracking`]: camelCase(
    `${window.location.hash}-page-${upperFirst(
      !value.includes('undefined') ? value : ''
    )}`
  ),
});

/**
 * @description Converts St Jude and variations to a non breaking word expression
 *   St. Jude => St.&nbsp;Jude
 *   St Jude => St&nbsp;Jude
 *   Saint Jude => Saint&nbsp;Jude
 *  Regex notes = Look ahead/behind assertions did not work in Safari so use three
 *    less complex replaces rather then one complex one.
 *  @param text
 */
export const wordWrapStJude = (text: string): string =>
  text
    .replace(/St Jude/gi, 'St&nbsp;Jude')
    .replace(/Saint Jude/gi, 'Saint&nbsp;Jude')
    .replace(/St. Jude/gi, 'St.&nbsp;Jude');

/**
 * @description Constructs a url with a hash
 * @param link trailing part of url
 *
 * */
export const buildHashLink = (link: string): string => {
  const { pathname, search } = window.location;
  const url = `${pathname}${search}#/${link}`;
  return url;
};

/**
 * @description The links to the app known as boundless app, which appear
 *  in the google play store and the apple store are only available for specific events.
 * Applies only to Heroes event and DIY events
 * @param eventId
 * @returns boolean
 *
 * */
// TODO: Identify a way to filter on the desired events.
// This is a temporary hard coded solution.
export const showBoundlessApps = (eventType?: string): boolean =>
  eventType?.toLowerCase() === EventTypes.HEROES ||
  eventType?.toLowerCase() === EventTypes.DIY ||
  eventType?.toLowerCase() === EventTypes.WALK;

export const isDiyEvent = (eventType?: string): boolean =>
  eventType?.toLowerCase() === EventTypes.DIY;

export const getShortDate = (
  originalDate: Date | string | undefined | null
) => {
  if (!originalDate) return '';

  const date =
    originalDate instanceof Date ? originalDate : new Date(originalDate);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const formattedDate = date
    .toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
    })
    .toUpperCase()
    .split(' ');

  return formattedDate;
};

/**
 * Parses a string containing one or more dates and returns the
 * corresponding startDate (earliest) and endDate (furthest).
 *
 * Supported formats: MM/DD/YYYY, YYYY-MM-DD, Mon DD, YYYY,
 * and Month DD, YYYY. Returns null values when the input is
 * invalid or ambiguous.
 *
 * @param value Date or date range text.
 * @returns Parsed startDate and endDate, or null values if invalid.
 */
export const parseDateRange = (value: string | undefined) => {
  if (!value) {
    return { startDate: null, endDate: null };
  }

  const text = String(value)
    .replace(/<br\s*\/?>/gi, ' ')
    .trim();

  // Matches supported date formats:
  // - ISO date: 2023-01-11
  // - ISO datetime: 2023-01-11T11:41:33.118-05:00
  // - US date: 01/11/2023
  // - Short month: Jan 11, 2023
  // - Long month: January 11, 2023
  const datePattern =
    /\b(?:\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?|\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{2}-\d{2}|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)[a-z]*\.?\s+\d{1,2},?\s*\d{4})\b/gi;

  const matches = text.match(datePattern) || [];

  if (matches.length === 0) {
    return { startDate: null, endDate: null };
  }

  const dates = matches
    .map((date) => new Date(date))
    .filter((date) => !Number.isNaN(date.getTime()));

  if (dates.length !== matches.length) {
    return { startDate: null, endDate: null };
  }

  // If anything remains, the input is considered invalid.
  const remaining = text.replace(datePattern, '').replace(/[-,\s]+/g, '');

  if (remaining.length > 0) {
    return { startDate: null, endDate: null };
  }

  const sortedDates = [...dates].sort((a, b) => a.getTime() - b.getTime());

  return {
    startDate: sortedDates[0] ?? null,
    endDate:
      sortedDates.length > 1 ? sortedDates[sortedDates.length - 1] : null,
  };
};
